// ============================================================
// SINGLE-MATCH ENGINE — moved to @starpoint/engine (#217).
//
// Moved out of the monolith first (incremental TS split, mirroring the
// elo/booking/km/seriespill/padel24 precedent), and now out of the browser
// app: the pad command API finalises a single match server-side, so the
// server has to run this code. Re-exported from the old path
// (apps/web/src/engine/singleMatch.ts) so every existing import keeps working
// and the move stays reviewable as a MOVE. The dependency closure was measured
// before moving: constants + elo, both already here.
//
// The function bodies below are unchanged. The ONLY addition is the optional
// `effects` argument on createSingleMatch — see the note at that function.
//
// Pure scoring + ELO logic for SINGLE MATCHES played outside tournaments
// (best-of-3 padel): winner detection from set results, total games for the
// margin-of-victory bonus, the per-match ELO change, applying that change to
// the player list, and building a storable single-match record.
//
// These are the implementations behind `Padel24Engine.singleMatch.*`. The
// singleMatch cluster was deliberately NOT moved when the rest of the Padel24
// engine was first extracted (see the note in src/engine/padel24.ts) — it was
// a distinct concern interleaved in the same monolith region.
//
// No storage, no React. They depend on the sibling ELO module
// (getPlayerKMultiplier, calcSingleMatchMov) and the shared EngineSettings
// runtime singleton — all one-way imports, no cycles. The runtime
// Padel24Engine.singleMatch wiring object points at these imported names.
//
// Loosely-shaped match/player/set objects are typed as `any` on purpose
// (strict:false, noImplicitAny:false).
//
// DEVIATION FROM THE VERBATIM ORIGINAL: createSingleMatch in the monolith
// referenced the module-local const `ELO_K_FACTOR` (= ELO_K_FACTOR_DEFAULT),
// and the decomposition used the imported constant directly — byte-different
// name, identical value.
//
// SINCE #264 IT USES NEITHER. The K-factor is derived from the club's
// CONFIGURED `settings.K_FACTOR`, so the compiled constant is no longer read
// here at all — it survives only as that setting's own default, in
// constants.ts. An untuned club therefore gets exactly the number it always
// got, by a different route.
// ============================================================

import { EngineSettings, type EngineSettingsValues } from './constants.js';
import { getPlayerKMultiplier, calcSingleMatchMov } from './elo.js';
import { resolveEffects, type EngineEffects } from './effects.js';

// Determine the winner based on the set results
// sets: [{ games1, games2 }, ...]
// Returns 'team1' | 'team2' | null (not finished)
export function calcSingleMatchWinner(sets) {
  let t1Sets = 0,
    t2Sets = 0;
  for (const s of sets) {
    if (s.games1 > s.games2) t1Sets++;
    else if (s.games2 > s.games1) t2Sets++;
  }
  if (t1Sets >= 2) return 'team1';
  if (t2Sets >= 2) return 'team2';
  return null;
}

// Sum of all games across the sets — used for the MOV calculation
export function sumGames(sets, teamIdx) {
  return sets.reduce((sum, s) => sum + (teamIdx === 1 ? s.games1 : s.games2), 0);
}

// Calculate the ELO change for a single match
// players: current player list (must contain all 4 players)
// match: { team1: {p1Id, p2Id}, team2: {p1Id, p2Id}, sets, kFactor }
// kFactor: allows reduced effect for shorter formats (default 64 = full)
// `settings` defaults to the live global, so an omitted argument reads exactly
// what this read before #243. The server passes a frozen snapshot (#243).
export function calcSingleMatchEloChange(
  players,
  match,
  now = Date.now(),
  settings: EngineSettingsValues = EngineSettings,
) {
  const t1p1 = players.find((p) => p.id === match.team1.p1Id);
  const t1p2 = players.find((p) => p.id === match.team1.p2Id);
  const t2p1 = players.find((p) => p.id === match.team2.p1Id);
  const t2p2 = players.find((p) => p.id === match.team2.p2Id);

  if (!t1p1 || !t1p2 || !t2p1 || !t2p2) {
    return { error: 'En eller flere spillere ble ikke funnet' };
  }

  const winner = calcSingleMatchWinner(match.sets || []);
  if (!winner) return { error: 'Kampen er ikke ferdig (ingen har vunnet 2 sett)' };

  const t1Elo = (t1p1.elo + t1p2.elo) / 2;
  const t2Elo = (t2p1.elo + t2p2.elo) / 2;
  const t1Games = sumGames(match.sets, 1);
  const t2Games = sumGames(match.sets, 2);

  // MOV based on the total number of games — uses a single-match-specific
  // formula that weights more strongly based on the share of games (Weighted Elo)
  const mov = calcSingleMatchMov(t1Games, t2Games);

  // Use the given kFactor — respect an explicit 0 (practice match = no ELO).
  // NB: must use != null, not ||, otherwise 0 falls back to the base K.
  //
  // `settings.K_FACTOR` here is still effectively unreachable, and now for a
  // harmless reason: `createSingleMatch` always resolves a kFactor onto the
  // match, and since #264 it resolves it FROM this same setting. The fallback
  // stays as the honest default for a match record that predates that or was
  // built by hand.
  //
  // A STORED MATCH KEEPS THE K IT WAS PLAYED UNDER. Re-tuning a club's K does
  // not reach back and change what old results were worth, which is the same
  // rule the rest of the engine follows.
  const K = match.kFactor != null ? match.kFactor : settings.K_FACTOR;

  // Asymmetric newbie boost: each team has its own multiplier
  const t1NewbieMul = Math.max(
    getPlayerKMultiplier(t1p1, now, 1.0, settings),
    getPlayerKMultiplier(t1p2, now, 1.0, settings),
  );
  const t2NewbieMul = Math.max(
    getPlayerKMultiplier(t2p1, now, 1.0, settings),
    getPlayerKMultiplier(t2p2, now, 1.0, settings),
  );

  // Kept verbatim from the monolith; dead in the original too (actual1/actual2
  // drive the math below), so silence the unused-var lint rather than alter bytes.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const t1Result = winner === 'team1' ? 'win' : 'loss';
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const t2Result = winner === 'team2' ? 'win' : 'loss';

  // Use the same formula as tournaments
  const expected1 = 1 / (1 + Math.pow(10, (t2Elo - t1Elo) / 400));
  const expected2 = 1 - expected1;
  const actual1 = winner === 'team1' ? 1 : 0;
  const actual2 = 1 - actual1;

  let t1Change = Math.round(K * mov * t1NewbieMul * (actual1 - expected1));
  let t2Change = Math.round(K * mov * t2NewbieMul * (actual2 - expected2));

  // Practice match (ELO weight 0%): the match is logged, but no ELO movement.
  // In that case skip the minimum-1 rule below.
  if (K === 0) {
    t1Change = 0;
    t2Change = 0;
  } else {
    // Minimum 1-point movement so the winner always gets something.
    if (winner === 'team1' && t1Change <= 0) {
      t1Change = 1;
      t2Change = -1;
    }
    if (winner === 'team2' && t2Change <= 0) {
      t2Change = 1;
      t1Change = -1;
    }
  }

  return {
    winner,
    t1Games,
    t2Games,
    t1EloBefore: t1Elo,
    t2EloBefore: t2Elo,
    t1Change,
    t2Change,
    mov,
    // New-player multipliers that were applied — so the history view can
    // compute base ELO (without boost) for the team pill, and the actual change
    // (with boost) for the player's 2x pill. Same pattern as tournament matches.
    t1NewbieMul,
    t2NewbieMul,
    t1p1Id: t1p1.id,
    t1p2Id: t1p2.id,
    t2p1Id: t2p1.id,
    t2p2Id: t2p2.id,
    // Individual pre-ELO for each player — used in the history view
    playerEloPre: {
      [t1p1.id]: t1p1.elo,
      [t1p2.id]: t1p2.elo,
      [t2p1.id]: t2p1.elo,
      [t2p2.id]: t2p2.elo,
    },
  };
}

// Apply a single match's ELO change to the player list
export function applySingleMatchElo(
  players,
  match,
  now = Date.now(),
  settings: EngineSettingsValues = EngineSettings,
) {
  const calc = calcSingleMatchEloChange(players, match, now, settings);
  if (calc.error) return { players, error: calc.error };

  return {
    players: players.map((p) => {
      const isT1 = p.id === calc.t1p1Id || p.id === calc.t1p2Id;
      const isT2 = p.id === calc.t2p1Id || p.id === calc.t2p2Id;
      if (!isT1 && !isT2) return p;
      const change = isT1 ? calc.t1Change : calc.t2Change;
      return {
        ...p,
        elo: p.elo + change,
        matchesPlayed: (p.matchesPlayed || 0) + 1,
        lastMatchAt: now,
      };
    }),
    calc,
  };
}

// Build a single-match record (ready for storage)
// eloWeightPct: ELO weight in percent (0-200). kFactor is derived if it is not
// given explicitly: kFactor = (eloWeightPct/100) * base. Respect 0 (practice match).
export function createSingleMatch(
  { team1, team2, sets, format, deuceMode, kFactor, eloWeightPct, playedAt, registeredBy },
  // #203: the clock and the id source as arguments. The defaults are exactly
  // what this function did before — `Date.now()` and
  // `Math.floor(Math.random() * 10000)` — so an omitted argument is
  // byte-identical to the previous behaviour, which is what the unchanged
  // characterization suite asserts. The server passes both so a retried
  // command reproduces the same id and the same timestamps instead of minting
  // a second match.
  effects?: EngineEffects,
) {
  const fx = resolveEffects(effects);
  // THE CLUB'S CONFIGURED K IS THE BASE (#264), not the compiled constant.
  //
  // This derived from `ELO_K_FACTOR_DEFAULT`, and because the function ALWAYS
  // writes a `kFactor` onto the match, the `settings.K_FACTOR` fallback in
  // `calcSingleMatch` was unreachable — every tournament format honoured a
  // club's tuning and the enkeltmatch never did. The CTO decision of
  // 2026-09-18 says that is a bug: «Enkeltkamper skal bruke den konfigurerte
  // K-faktoren.»
  //
  // `eloWeightPct` SCALES the configured K, which is the reading recorded on
  // #264: K=40 at 50% is an effective 20. The percentage keeps meaning «share
  // of this club's full weight» rather than «share of a number nobody chose».
  //
  // ONE BASE FOR BOTH DIRECTIONS. The percentage below is the inverse of the
  // derivation above, so the two must read the same value — deriving K from
  // the setting while reverse-deriving the percentage from the constant would
  // round-trip a weight into a different number and show it back to the user.
  const base = fx.settings.K_FACTOR;
  // Derive kFactor from eloWeightPct if kFactor is not set explicitly.
  let resolvedK;
  if (kFactor != null) resolvedK = kFactor;
  else if (eloWeightPct != null) resolvedK = (eloWeightPct / 100) * base;
  else resolvedK = base; // backwards-compat: no weight given = 100%
  // Also store the percentage for display/editing (derive from K if only K came in)
  const resolvedPct =
    eloWeightPct != null
      ? eloWeightPct
      : kFactor != null
        ? Math.round((kFactor / base) * 100)
        : 100;
  return {
    id: `sm_${fx.now}_${fx.uniqueSuffix()}`,
    type: 'single', // distinguishes from tournament matches
    format: format || 'best_of_3',
    // Counting mode at 40-40 (advantage/golden/starpoint). Metadata for live
    // scoring + display; the ELO calc reads only the final games, so it's unaffected.
    deuceMode: deuceMode || 'advantage',
    kFactor: resolvedK,
    eloWeightPct: resolvedPct,
    playedAt: playedAt || fx.now,
    registeredBy: registeredBy || 'admin',
    team1: { p1Id: team1.p1Id, p2Id: team1.p2Id },
    team2: { p1Id: team2.p1Id, p2Id: team2.p2Id },
    sets: sets.map((s) => ({ games1: s.games1, games2: s.games2 })),
  };
}
